import React, { useCallback, useEffect, useMemo } from "react";
import { StyleSheet } from "react-native";
import { EffectiveFeeInput } from "../../../__generated__/graphql";
import Modal from "../../Modal";
import ScreenCard from "../../../screens/ScreenCard";
import SimpleHeader from "../../SimpleHeader";
import VStack from "../../../ui_atoms/VStack";
import { Controller, useForm } from "react-hook-form";
import TextField from "../../TextField";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useTheme } from "../../../hooks/use-theme";
import Text from "../../Text";
import { useChainInputFocus } from "../../../hooks/use-chain-input-focus";
import { onCurrencyChange } from "../../../utils";

export type CreateEffectiveFeeFormData = {
    amount: number | null;
    description?: string;
};

type CreateEffectiveFeeModalProps = {
    onCreate: (effectiveFee: EffectiveFeeInput) => void;
    onClose: () => void;
    visible: boolean;
    initialValues?: CreateEffectiveFeeFormData;
};

const CreateEffectiveFeeModal: React.FC<CreateEffectiveFeeModalProps> = ({
    onClose,
    onCreate,
    visible,
    initialValues,
}) => {
    const styles = useStyles();
    const { colors, fonts } = useTheme();
    const i18n = useI18n();

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isValid },
    } = useForm<CreateEffectiveFeeFormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            amount: null,
            description: "",
        },
    });
    useEffect(() => {
        if (visible) {
            if (initialValues) {
                setValue("amount", initialValues.amount);
                setValue("description", initialValues.description);
            }
        } else {
            reset();
        }
    }, [visible, reset, initialValues, setValue]);

    const handleCreateEffectiveFee = useCallback(
        (formData: CreateEffectiveFeeFormData) => {
            onCreate({
                amount: isNaN(formData.amount || 0) ? 0 : (formData.amount || 0) * 100,
                description: formData.description!,
            });
        },
        [onCreate],
    );

    const { chainInputFocus } = useChainInputFocus();

    return (
        <Modal visible={visible} onClose={onClose} hideCloseButton animationType="slide">
            <SimpleHeader title={i18n.t("effective_fee.title")} onClose={onClose} mode="small" />
            <ScreenCard
                buttons={[
                    {
                        onPress: handleSubmit(handleCreateEffectiveFee),
                        mode: "secondary",
                        isValid: isValid,
                        title: !initialValues ? i18n.t("add") : i18n.t("edit"),
                    },
                ]}
            >
                <VStack gap={1} h="2/3">
                    <Controller
                        name="amount"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => {
                            return (
                                <VStack gap="1/3">
                                    <Text style={styles.amountTitle}>
                                        {i18n.t("effective_fee.amount_title")}
                                    </Text>
                                    <TextField
                                        value={value?.toString()}
                                        onChangeText={onCurrencyChange(onChange)}
                                        onBlur={onBlur}
                                        error={error?.message}
                                        inputStyle={fonts.headlineMedium}
                                        showErrorText
                                        left="CHF"
                                        leftColor={colors.fieldLabel}
                                        keyboardType="decimal-pad"
                                        textAlign="right"
                                        {...chainInputFocus(0)}
                                    />
                                </VStack>
                            );
                        }}
                    />
                    <Controller
                        name="description"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                            <TextField
                                label={i18n.t(`effective_fee.field_description`)}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={error?.message}
                                showErrorText
                                multiline
                                {...chainInputFocus(1)}
                            />
                        )}
                    />
                </VStack>
            </ScreenCard>
        </Modal>
    );
};

const useStyles = () => {
    const { fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                amountTitle: {
                    ...fonts.bodyMedium,
                },
            }),
        [fonts.bodyMedium],
    );
};

export default CreateEffectiveFeeModal;
